import { QIcon_caf_accuracy, QIcon_caf_complexity, QIcon_caf_fluency, QIcon_caf_pronunciation } from '@repo/icons/index'
import classNames from 'classnames'
import { CAF_ANALYSIS_TYPE_MAP } from '../../../../model/caf.interface'
import { ILessonDiff } from '../../../../modules/apis/student/lesson'
import { BOX_BORDER_CLX } from '../../../../modules/consts/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useCafLessonDiffData from '../../../../modules/hooks/webview/caf/common/useCafLessonDiffData'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../modules/vars'

interface ProgressBoxType {
  title: string
  type: CAF_ANALYSIS_TYPE_MAP
  levelRough: number
  levelExact: number
  totalNum: number
  onClick?: () => void
  mr: number
  diffData: ILessonDiff[]
  componentTag: number
}

export const ProgressBar = ({
  levelExact,
  totalNum,
  Icon,
  hideFraction = false,
}: {
  levelExact: number
  totalNum: number
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  hideFraction?: boolean
}) => {
  const isMobile = useIsMobile()
  const dividerArray = Array.from({ length: totalNum }, (_, i) => (i + 1) * (100 / totalNum))

  return isMobile ? (
    <div className={barStyles.container}>
      <div className={barStyles.bg}></div>
      <div className={barStyles.active} style={{ width: (levelExact / totalNum) * 100 + '%' }}>
        {!hideFraction && (
          <div className="z-5000 absolute right-[-6px] top-[-23px] text-[12px] font-normal text-gray-400">
            {levelExact}
          </div>
        )}
      </div>
      {/* DIVIDER */}
      {dividerArray?.map((dividerValue, idx) => (
        <div key={idx + 'dividerValue'} className={barStyles.divider} style={{ left: dividerValue + '%' }}></div>
      ))}
      <Icon className="absolute right-[-28px] top-[-12px]" width={20} height={20} />
    </div>
  ) : (
    <div className={barStyles.container_pc}>
      <div className={barStyles.bg_pc}></div>
      <div className={barStyles.active_pc} style={{ width: (levelExact / totalNum) * 100 + '%' }}>
        {!hideFraction && (
          <div className="z-5000 absolute right-[-6px] top-[-22px] text-[12px] font-normal text-gray-400">
            {levelExact}
          </div>
        )}
      </div>
      {/* DIVIDER */}
      {dividerArray?.map((dividerValue, idx) => (
        <div key={idx + 'dividerValue'} className={barStyles.divider_pc} style={{ left: dividerValue + '%' }}></div>
      ))}
      <Icon className="absolute right-[-32px] top-[-12px]" width={24} height={24} />
    </div>
  )
}

export const ProgressBarNoDetail = ({ levelExact, totalNum, hideFraction = false }) => {
  const isMobile = useIsMobile()
  const dividerArray = Array.from({ length: totalNum }, (_, i) => (i + 1) * (100 / totalNum))

  return isMobile ? (
    <div className={barStyles.container_nodetail}>
      <div className={barStyles.bg}></div>
      <div className={barStyles.active} style={{ width: (levelExact / totalNum) * 100 + '%' }}>
        {!hideFraction && (
          <div className="z-5000 absolute right-[-6px] top-[-23px] text-[12px] font-normal text-gray-400">
            {levelExact}
          </div>
        )}
      </div>
      {/* DIVIDER */}
      {dividerArray?.map((dividerValue, idx) => (
        <div key={idx + 'dividerValue'} className={barStyles.divider} style={{ left: dividerValue + '%' }}></div>
      ))}
    </div>
  ) : (
    <div className={barStyles.container_pc_nodetail}>
      <div className={barStyles.bg_pc}></div>
      <div className={barStyles.active_pc} style={{ width: (levelExact / totalNum) * 100 + '%' }}>
        {!hideFraction && (
          <div className="z-5000 absolute right-[-6px] top-[-22px] text-[12px] font-normal text-gray-400">
            {levelExact}
          </div>
        )}
      </div>
      {/* DIVIDER */}
      {dividerArray?.map((dividerValue, idx) => (
        <div key={idx + 'dividerValue'} className={barStyles.divider_pc} style={{ left: dividerValue + '%' }}></div>
      ))}
    </div>
  )
}

const imgSource = {
  complexity: (props) => <QIcon_caf_complexity {...props} className="[&>*]:fill-current" />,
  accuracy: (props) => <QIcon_caf_accuracy {...props} className="[&>*]:fill-current" />,
  fluency: (props) => <QIcon_caf_fluency {...props} className="[&>*]:fill-current" />,
  pronunciation: (props) => <QIcon_caf_pronunciation {...props} className="[&>*]:fill-current" />,
}

const ProgressBox = ({
  title,
  levelExact,
  totalNum,
  onClick,
  levelRough,
  mr = 0,
  diffData,
  componentTag,
}: ProgressBoxType) => {
  const { t, locale } = useTranslation()
  const isMobile = useIsMobile()
  const { diffDataAsTag, isFirst, Icon: DiffIcon } = useCafLessonDiffData(diffData, componentTag)
  const Icon = imgSource[title]
  const showBetaBadge = title === 'accuracy' || title === 'pronunciation'

  if (!Icon) {
    console.error(`Icon for title "${title}" is not defined.`)
    return null
  }

  if (isMobile) {
    return (
      <div className={classNames(boxStyles.container1, `${BOX_BORDER_CLX}`)} onClick={onClick}>
        {/* HEADER */}
        <div className={boxStyles.container2}>
          <div className="flex items-center justify-between gap-[4px]">
            <Icon width={20} height={20} fill={COLORS.GRAY800} />
            <h3 className={boxStyles.h3}>{title}</h3>
            {locale === 'ko' && <div className={boxStyles.subtitle}>{t(n_caf.highlight_list[title + '2'])}</div>}
            {showBetaBadge && (
              <div className="rounded-[4px] bg-red-50 px-[6px] py-[2px] text-[10px] font-bold text-red-400">Beta</div>
            )}
          </div>
          <span>
            <span className={boxStyles.spanActive}>{t(n_caf.overview.level_score)} </span>
            <span className={boxStyles.spanActive}>{levelRough}</span>
            <span className={boxStyles.spanDeact}>/{totalNum - 1}</span>
          </span>
        </div>
        {/* PROGRESS BAR */}
        {!isFirst ? (
          <ProgressBar levelExact={levelExact} totalNum={totalNum} Icon={DiffIcon} />
        ) : (
          <ProgressBarNoDetail levelExact={levelExact} totalNum={totalNum} />
        )}
      </div>
    )
  } else {
    return (
      <div className={classNames(boxStyles.container1_pc, `border-1 box-border border-gray-200`)} onClick={onClick}>
        {/* HEADER */}
        <div className={boxStyles.container2_pc}>
          <div className="flex items-center gap-[4px]">
            <Icon width={20} height={20} fill={COLORS.GRAY800} />
            <h3 className={boxStyles.h3_pc}>{title}</h3>
            {locale === 'ko' && <div className={boxStyles.subtitle}>{t(n_caf.highlight_list[title + '2'])}</div>}
            {showBetaBadge && (
              <div className="rounded-[4px] bg-red-50 px-[6px] py-[2px] text-[10px] font-bold text-red-400">Beta</div>
            )}
          </div>
          <div className="flex flex-row items-end justify-end">
            <span className={boxStyles.spanActive_pc}>{t(n_caf.overview.level_score) + ' '}</span>
            <span className={boxStyles.spanActive_pc}>{levelRough}</span>
            <span className={boxStyles.spanDeact_pc}>/{totalNum - 1}</span>
          </div>
        </div>
        {/* PROGRESS BAR */}
        {!isFirst ? (
          <ProgressBar levelExact={levelExact} totalNum={totalNum} Icon={DiffIcon} />
        ) : (
          <ProgressBarNoDetail levelExact={levelExact} totalNum={totalNum} />
        )}
      </div>
    )
  }
}

export default ProgressBox

const barStyles = {
  container_nodetail: `relative mt-[12px] h-[8px] w-[100%] overflow-visible`,
  container_pc_nodetail: `relative mt-[12px] h-[12px] w-[100%] overflow-visible`,
  container: `w-[calc(100%-28px)] relative mt-[12px] mr-[28px] h-[8px] overflow-visible`,
  container_pc: `w-[calc(100%-32px)] relative mt-[12px] mr-[32px] h-[12px] overflow-visible`,
  bg: `absolute top-0 left-0 z-[1] flex h-[8px] w-full overflow-hidden rounded-full bg-gray-200`,
  bg_pc: `absolute top-0 left-0 z-[1] flex h-[12px] w-full overflow-hidden rounded-full bg-gray-200`,
  active: `absolute top-0 left-0 z-[2] flex h-[8px] overflow-visible rounded-full bg-purple-500 transition-colors`,
  active_pc: `absolute top-0 left-0 z-[2] flex h-[12px] overflow-visible rounded-full bg-purple-500 transition-colors`,
  divider: `absolute top-0 z-[3] h-[8px] w-[1px] bg-white`,
  divider_pc: `absolute top-0 z-[3] h-[12px] w-[1px] bg-white`,
}

const boxStyles = {
  container1: `border-1 mb-[8px] flex h-[85px] flex-col items-start justify-between rounded-[8px] border-gray-100 p-[12px] shadow-sm`,
  container1_pc: `box-border flex h-[104px] w-full cursor-pointer flex-col justify-between rounded-[8px] p-[16px] shadow-sm`,
  container2: `text-h5 flex w-[100%] flex-row items-center justify-between font-medium`,
  container2_pc: `flex w-[100%] flex-row items-start justify-between font-medium`,
  h3: `text-[14px] font-bold capitalize text-black`,
  h3_pc: `items-center text-[16px] font-bold capitalize text-gray-700`,
  subtitle: `ml-[1px] text-[14px] text-gray-500`,
  spanActive: `text-[18px] font-bold text-purple-500`,
  spanActive_pc: `ml-[4px] text-[20px] font-bold text-purple-500`,
  spanDeact: `text-[18px] text-gray-200`,
  spanDeact_pc: `text-[14px] font-bold leading-6 text-gray-200`,
  img: `ml-[8px] inline`,
  img_pc: `ml-[8px] mb-[6px] inline`,
}
